import { ErrorState } from "$components/error-state";
import { useLazyUser } from "$global-providers/user-provider";
import * as Sentry from "@sentry/react";
import React from "react";

/**
 * This component is a wrapper around Sentry.ErrorBoundary that provides a fallback UI. It should be used
 * at any location an Errory Boundary is needed.
 * @param name The name of the error boundary. This will be sent to Sentry as a tag.
 * @param restProps Props to pass to the Sentry.ErrorBoundary component, plus the {name} of the boundary.
 * @returns an Elephant styled ErrorBoundary
 */
export const ErrorBoundary: FC<
  {
    name: string;
  } & React.ComponentProps<typeof Sentry.ErrorBoundary>
> = ({
  name,
  fallback = (error) => (
    <ErrorState
      variant="boundary"
      details={{
        name: error.error.name,
        message: error.error.message,
        stack: error.componentStack,
      }}
    />
  ),
  ...restProps
}) => {
  const user = useLazyUser();
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("boundary.name", name);
      }}
      showDialog
      dialogOptions={{
        user: {
          name: [user?.firstName, user?.lastName].join(" "),
          email: user?.email,
        },
      }}
      fallback={fallback}
      {...restProps}
    />
  );
};
